import { supabase } from '@/lib/supabase';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

// Facebook App ID (public — il voyage déjà en clair dans le bundle natif :
// FacebookAppID dans ios/App/App/Info.plist, facebook_app_id dans
// android/app/src/main/res/values/strings.xml). L'env ne sert plus que
// d'override ponctuel, comme GOOGLE_WEB_CLIENT_ID dans google-auth.service.ts.
//
// PIÈGE, cause de 100 % des échecs Facebook en production jusqu'au 16/08/2026 :
// les .env.* sont gitignorés et AUCUNE chaîne de build de store ne fournissait
// VITE_FACEBOOK_APP_ID — ni .github/workflows/deploy-android.yml, qui
// reconstruit .env.production depuis les secrets GitHub sans cette clé, ni
// Xcode Cloud. La constante valait donc '' dans tous les builds publiés, et le
// garde ci-dessous levait AVANT le moindre appel au SDK Meta : 21 tentatives,
// 21 échecs, zéro inscription, sur iOS comme sur Android. La validation device
// du 22/06/2026 passait parce qu'un build local, lui, lit bien .env.production.
// Sans valeur par défaut ici, le bouton redevient mort au prochain build de
// store qui oublie la variable.
const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID || '2222636048272132';

let socialLoginInitPromise: Promise<void> | null = null;

// The @capgo plugin does NOT read capacitor.config.ts's SocialLogin block — it
// must be initialized at runtime (same lesson as Google, tâche 17579292).
function ensureFacebookInitialized(): Promise<void> {
    if (!socialLoginInitPromise) {
        socialLoginInitPromise = SocialLogin.initialize({
            facebook: { appId: FACEBOOK_APP_ID },
        }).catch((error) => {
            socialLoginInitPromise = null; // allow retry on next attempt
            throw error;
        });
    }
    return socialLoginInitPromise;
}

function resolveRedirectUrl(): string {
    if (Capacitor.isNativePlatform()) return 'com.withharmonia.app://auth/callback';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocalhost
        ? `${window.location.origin}/auth/callback`
        : 'https://app.withharmonia.com/auth/callback';
}

export const facebookAuthService = {
    /**
     * Sign in with Facebook. Native (iOS/Android) uses the @capgo social-login
     * plugin → idToken → Supabase signInWithIdToken. Web uses Supabase's OAuth
     * redirect (the callback is handled provider-agnostically by
     * AuthCallbackPage / the App.vue deep-link handler).
     */
    async signInWithFacebook(): Promise<void> {
        try {
            if (Capacitor.isNativePlatform()) {
                await this.signInWithFacebookNative();
            } else {
                await this.signInWithFacebookWeb(resolveRedirectUrl());
            }
        } catch (error) {
            console.error('❌ [Facebook Auth] Sign in error:', error);
            throw error;
        }
    },

    async signInWithFacebookNative(): Promise<void> {
        if (!FACEBOOK_APP_ID) {
            throw new Error('Facebook App ID not configured. Set VITE_FACEBOOK_APP_ID and configure the Meta app + Supabase Facebook provider.');
        }
        if (!SocialLogin || typeof SocialLogin.login !== 'function') {
            throw new Error('SocialLogin plugin is not available. Ensure @capgo/capacitor-social-login is installed and synced.');
        }

        await ensureFacebookInitialized();

        const result = await SocialLogin.login({
            provider: 'facebook',
            options: {
                permissions: ['public_profile', 'email'],
            },
        });

        // Plugin returns { provider: 'facebook', result: { idToken, accessToken, profile } }
        const fbResult = ('result' in result && result.result) ? result.result : result;
        type FacebookAuthResult = { idToken?: string | null; accessToken?: { token?: string } | null };
        const authResult = fbResult as FacebookAuthResult;
        const token = authResult.idToken || authResult.accessToken?.token;

        if (!token) {
            console.error('❌ [Facebook Auth Native] No token in response:', JSON.stringify(result));
            throw new Error('No token received from Facebook');
        }

        const { error } = await supabase.auth.signInWithIdToken({
            provider: 'facebook',
            token,
        });
        if (error) {
            console.error('❌ [Facebook Auth Native] Supabase sign in error:', error);
            throw error;
        }
        console.log('✅ [Facebook Auth Native] Signed in');
    },

    async signInWithFacebookWeb(redirectUrl: string): Promise<void> {
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'facebook',
            options: { redirectTo: redirectUrl },
        });
        if (error) {
            console.error('❌ [Facebook Auth Web] OAuth error:', error);
            throw error;
        }
        if (data?.url) {
            window.location.href = data.url;
        }
    },
};
